import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { affordabilityProxyApi } from 'api/affordability';
import { AffordabilityEvaluationResponse } from 'api/affordability/types';
import { CalculatorBackButton, CalculatorSubmitButton } from 'components/common';
import { saveAffordabilityFormToLocalStorage, setLastAffordabilityCalcPage } from 'utils/localStorage';
import { employmentTypesWithIncome } from '../ApplicantDetailsPage/helpers';
import { applicantDataFormatter, applicantDefaultValues, formDataFormatter } from '../helpers';
import { AffordabilityCalculatorForm, CalculatorPages, EmploymentTypesEnum, MainTypesEnum } from '../types';
import {
  benefitsAndCreditsFields,
  creditCommitmentsFields,
  expenditureFields,
  financeDetailsFieds,
  getFieldsByEmploymentType,
  otherIncomeFields,
} from './helpers';
import { FinanceDetailsReviewQuestions, ApplicantDetailsReviewQuestions } from './questions';
import { ReviewBlock } from './ReviewBlock';
import { ReviewPageProps } from './types';
import { scrollToTop, stageChecker } from 'utils/general';
import { ServerError } from '../../../errors/server-error';

export const ReviewPage: FC<ReviewPageProps> = ({ isSubmitHidden = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const form = useFormContext<AffordabilityCalculatorForm>();
  const formFields = form.watch();
  const { applicants } = formFields;
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: getAffordability, isLoading: isAffordabilityLoading } = useMutation(
    affordabilityProxyApi.getAffordabilityEvaluation,
    {
      onSuccess: (data: AffordabilityEvaluationResponse[]) => redirectToResultsPage(data),
      onError: (error: ServerError) => {
        console.log(error.status);

        if (error.status === 404) {
          return redirectToResultsPage([]);
        }

        if (error.status === 500) {
          return redirectToResultsPage([], true);
        }

        return enqueueSnackbar('Request failed', { variant: 'error' });
      },
    },
  );

  const redirectToPage = (section: CalculatorPages, applicantNumber?: number) => {
    searchParams.set('section', section);
    if (applicantNumber !== undefined) {
      searchParams.set('applicantNumber', String(applicantNumber));
    }
    setSearchParams(searchParams);
    scrollToTop();
  };

  const redirectToResultsPage = (data: AffordabilityEvaluationResponse[], isError = false) => {
    setLastAffordabilityCalcPage(CalculatorPages.results);
    stageChecker(() => console.log('results from BE ', data));
    form.setValue('results', data);
    form.setValue('isTechIssue', isError);
    saveAffordabilityFormToLocalStorage({ ...formFields, results: data });
    redirectToPage(CalculatorPages.results);
  };

  const submitFormData = async () => {
    const dto = formDataFormatter(formFields);

    if (dto.applicants.length === 1) {
      dto.applicants.push(applicantDataFormatter(applicantDefaultValues));
    }

    getAffordability(dto);
  };

  return (
    <>
      <ReviewBlock
        reviewRowKeys={financeDetailsFieds}
        onEditClick={() => redirectToPage(CalculatorPages.financeDetails)}
        titleKey={FinanceDetailsReviewQuestions.titleKey}
      />
      {applicants.map((applicant, applicantIndex) => {
        const primaryEmploymentType = applicant?.primaryIncome.employmentType;
        const incomeFields = getFieldsByEmploymentType(
          primaryEmploymentType ?? null,
          MainTypesEnum.primaryIncome,
          applicantIndex,
        );
        const isSecondIncome = applicant?.isSecondIncome === 'yes';
        const secondEmploymentType = applicant?.secondIncome.employmentType;
        const secondIncomeFields = getFieldsByEmploymentType(
          secondEmploymentType ?? null,
          MainTypesEnum.secondIncome,
          applicantIndex,
        );
        const isApplicantWithIncome = employmentTypesWithIncome.includes(primaryEmploymentType as EmploymentTypesEnum);

        return (
          <div key={applicantIndex}>
            <ReviewBlock
              reviewRowKeys={incomeFields}
              onEditClick={() => redirectToPage(CalculatorPages.applicantDetails, applicantIndex)}
              titleKey={ApplicantDetailsReviewQuestions.titleKey}
              titleValues={{ applicantNumber: applicantIndex + 1 }}
              subTitleKey={ApplicantDetailsReviewQuestions.income.titleKey}
              subTitleValues={{ incomeNumber: 1 }}
            />
            {isSecondIncome && (
              <ReviewBlock
                key={applicantIndex}
                reviewRowKeys={secondIncomeFields}
                onEditClick={() => redirectToPage(CalculatorPages.applicantDetails, applicantIndex)}
                subTitleKey={ApplicantDetailsReviewQuestions.income.titleKey}
                subTitleValues={{ incomeNumber: 2 }}
              />
            )}
            {isApplicantWithIncome && (
              <>
                <ReviewBlock
                  reviewRowKeys={otherIncomeFields(applicantIndex)}
                  hideEditButton={true}
                  subTitleKey={ApplicantDetailsReviewQuestions.otherIncome.titleKey}
                />
                <ReviewBlock
                  reviewRowKeys={benefitsAndCreditsFields(applicantIndex)}
                  hideEditButton={true}
                  subTitleKey={ApplicantDetailsReviewQuestions.benefitsAndCredits.titleKey}
                />
              </>
            )}
          </div>
        );
      })}
      <ReviewBlock
        reviewRowKeys={creditCommitmentsFields}
        onEditClick={() => redirectToPage(CalculatorPages.outgoings)}
        subTitleKey={ApplicantDetailsReviewQuestions.creditCommitments.subTitleKey}
      />
      <ReviewBlock
        reviewRowKeys={expenditureFields}
        onEditClick={() => redirectToPage(CalculatorPages.outgoings)}
        subTitleKey={ApplicantDetailsReviewQuestions.expenditure.subTitleKey}
      />
      {!isSubmitHidden && (
        <div className={'xs:my-10 xs:flex-col-reverse flex w-full justify-between md:mt-24 md:flex-row'}>
          <CalculatorBackButton onClick={() => redirectToPage(CalculatorPages.outgoings)} />
          <div className={'h-8 w-full md:hidden'}></div>
          <CalculatorSubmitButton
            isLoading={isAffordabilityLoading}
            onClick={submitFormData}
            circularProgressClassName={'flex justify-center mx-16'}
            circularProgressProps={{ color: 'primary' }}
          />
        </div>
      )}
    </>
  );
};
